import React, {
  useEffect,
  useCallback,
  useState,
} from 'react';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';

// Utils
import { getIsEmailValid } from 'utils/common';
import { useInput } from 'utils/hooks/useInput';

import { INPUT_ERRORS } from 'constants/common';
import { MODAL_NAMES } from 'constants/modal';
import { openPopup } from 'actions';
import { Modal } from '@beef/ui-kit';
import { useIsMobileWidth } from 'utils/hooks/useIsMobile';
import {
  confirmDetalizationPersonalData,
  sendDetalizationPersonalDataConfirmationCode,
  setConfirmDetalizationPersonalDataError,
  setUpdateDetalizationPersonalDataError,
  setDetalizationConfirmData,
  fetchDetalizationConfirmMailData, updateDetalizationPersonalData,
} from '../../store/personalDataStore/actions';

// Components
import {
  EmailForm,
  ConfirmationForm,
  SuccessSection,
} from './components';

const EMAIL = 'EMAIL';

const MODAL_STATES = {
  EMAIL: 'EMAIL',
  PASS_CODE: 'PASS_CODE',
  SUCCESS: 'SUCCESS',
};

export const EmailModal = () => {
  const dispatch = useDispatch();

  const isMobile = useIsMobileWidth();

  const {
    email,
    modalName,
    currentDetalizationModal,
    isEmailUpdating,
    isEmailConfirmed,
    isEmailUpdatingError,
    isPasscodeConfirming,
    isPasscodeConfirmed,
    isPasscodeConfirmingError,
    isIncorrectConfirmationCode,
    isSendingConfirmationCode,
    isSendingConfirmationCodeError,
  } = useSelector(({
    detalizationNew,
    application,
  }) => ({
    email: detalizationNew.personalData.emailAddress,
    modalName: application.popup,
    isEmailConfirmed: detalizationNew.personalData.isEmailConfirmed,
    currentDetalizationModal: detalizationNew.currentDetalizationModal,
    isEmailUpdating: detalizationNew.personalData.isDataUpdating,
    isEmailUpdatingSuccess: detalizationNew.personalData.isDataUpdatingSuccess,
    isEmailUpdatingError: detalizationNew.personalData.isDataUpdatingError,
    isPasscodeConfirming: detalizationNew.personalData.isDataConfirming,
    isPasscodeConfirmed: detalizationNew.personalData.isDataConfirmingSuccess,
    isPasscodeConfirmingError: detalizationNew.personalData.isDataConfirmingError,
    isIncorrectConfirmationCode: detalizationNew.personalData.isIncorrectConfirmationCode,
    isSendingConfirmationCode: detalizationNew.personalData.isSendingConfirmationCode,
    isSendingConfirmationCodeError: detalizationNew.personalData.isSendingConfirmationCodeError,
  }), shallowEqual);

  const [isOpen, setIsOpen] = useState(false);
  const [modalState, setModalState] = useState(MODAL_STATES.EMAIL);

  useEffect(() => {
    setIsOpen(MODAL_NAMES.EMAIL === modalName);
  }, [modalName]);

  const emailInput = useInput({
    initialState: email,
    errorText: INPUT_ERRORS.EMAIL,
    invalidHandler: getIsEmailValid,
  });

  const formDisabled = !getIsEmailValid(emailInput.value)
    || (emailInput.value === email && isEmailConfirmed);

  const confirmationCodeInput = useInput({
    initialState: '',
    errorText: INPUT_ERRORS.PASS_CODE,
    invalidHandler: (confirmationCode) => confirmationCode.length !== 0,
  });

  const onBackToDetalization = useCallback(() => {
    emailInput.setValue(email);
    confirmationCodeInput.setValue('');
    setModalState(MODAL_STATES.EMAIL);

    dispatch(setDetalizationConfirmData());
    dispatch(openPopup(currentDetalizationModal));
  }, [dispatch, emailInput, confirmationCodeInput, currentDetalizationModal]);

  const onBackToInitialState = useCallback(() => {
    confirmationCodeInput.setValue('');

    setModalState(MODAL_STATES.EMAIL);
  }, [confirmationCodeInput]);

  const setInputState = () => {
    emailInput.setValue(email);
    confirmationCodeInput.setValue('');
    setModalState(MODAL_STATES.EMAIL);
  };

  const onCloseModal = useCallback(() => {
    setInputState();

    dispatch(setDetalizationConfirmData());
    dispatch(openPopup(null));
  }, [dispatch, emailInput, confirmationCodeInput, currentDetalizationModal]);

  const onClosePassCodeModal = useCallback(() => {
    setInputState();

    dispatch(fetchDetalizationConfirmMailData(false));
    dispatch(setDetalizationConfirmData());
    dispatch(openPopup(null));
  }, [dispatch, emailInput, confirmationCodeInput, currentDetalizationModal]);

  const submitEmailHandler = useCallback(
    (event) => {
      event.preventDefault();
      if (!emailInput.isValid) {
        return emailInput.setErrorMessage(INPUT_ERRORS.EMAIL);
      }
      setModalState(MODAL_STATES.PASS_CODE);

      dispatch(updateDetalizationPersonalData({
        type: EMAIL,
        value: emailInput.value,
      }));
    },
    [emailInput, dispatch],
  );

  const onSendConfirmationCode = useCallback(
    () => dispatch(sendDetalizationPersonalDataConfirmationCode(EMAIL)),
    [dispatch],
  );

  const submitPasscodeHandler = useCallback(
    (event) => {
      event.preventDefault();

      if (!confirmationCodeInput.isValid) {
        return confirmationCodeInput.setErrorMessage(INPUT_ERRORS.PASS_CODE);
      }

      dispatch(confirmDetalizationPersonalData({
        confirmationCode: confirmationCodeInput.value,
        type: EMAIL,
      }));
    },
    [confirmationCodeInput, dispatch],
  );

  useEffect(() => {
    if (isPasscodeConfirmed) {
      setModalState(MODAL_STATES.SUCCESS);
      dispatch(fetchDetalizationConfirmMailData(true));
    }

    if (!isPasscodeConfirmed && isPasscodeConfirmingError) {
      dispatch(fetchDetalizationConfirmMailData(false));
    }
  }, [isPasscodeConfirmed, onCloseModal]);

  useEffect(() => {
    if (isEmailUpdatingError) {
      dispatch(setUpdateDetalizationPersonalDataError(false));
    }

    if (isPasscodeConfirmingError) {
      dispatch(setConfirmDetalizationPersonalDataError(false));
    }
  }, [
    emailInput.value,
    confirmationCodeInput.value,
  ]);

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      size="xs"
      onClose={modalState === MODAL_STATES.PASS_CODE ? onClosePassCodeModal : onCloseModal}
    >
      <div>
        {modalState === MODAL_STATES.EMAIL && (
          <EmailForm
            isEmailConfirmed={isEmailConfirmed}
            onGoBack={onBackToDetalization}
            onSubmit={submitEmailHandler}
            isMobile={isMobile}
            disabled={formDisabled}
            email={email}
            isFetching={isEmailUpdating}
            isError={isEmailUpdatingError}
            inputData={emailInput}
          />
        )}
        {modalState === MODAL_STATES.PASS_CODE && (
          <ConfirmationForm
            onGoBack={onBackToInitialState}
            onSubmit={submitPasscodeHandler}
            onReloadCode={onSendConfirmationCode}
            isMobile={isMobile}
            email={email}
            inputData={confirmationCodeInput}
            isCodeSending={isSendingConfirmationCode}
            isCodeSendingError={isSendingConfirmationCodeError}
            isChecking={isPasscodeConfirming}
            isCheckingError={isPasscodeConfirmingError}
            isIncorrectConfirmationCode={isIncorrectConfirmationCode}
          />
        )}
        {modalState === MODAL_STATES.SUCCESS && (
          <SuccessSection onClick={onBackToDetalization} />
        )}
      </div>
    </Modal>
  );
};
